import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Кнопка «Войти через Google» на основе Google Identity Services.
 *
 * Google отдаёт браузеру ID-токен, а проверяет его сервер (/api/auth/google.php).
 * Client secret на фронтенде не нужен и здесь его быть не должно.
 *
 * Скрипт Google доступен не везде и не всегда: если он не загрузился, показываем
 * это прямо, чтобы человек не гадал, почему кнопки нет, и ушёл входить через
 * Telegram.
 */

const CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "1067539732545-jh38m269i719gce3sei0mo3dj7ojdk3q.apps.googleusercontent.com";
const GSI_SCRIPT = "https://accounts.google.com/gsi/client";

interface Props {
  onCredential: (credential: string) => void;
  disabled?: boolean;
  text?: "signin_with" | "continue_with";
}

function loadGsiScript(): Promise<void> {
  if (typeof document === "undefined") {
    return Promise.reject(new Error("no document"));
  }

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${GSI_SCRIPT}"]`
  );
  if (existing && (window as any).google?.accounts?.id) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = existing || document.createElement("script");
    script.src = GSI_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("gsi script failed"));
    if (!existing) {
      document.head.appendChild(script);
    }
  });
}

const GoogleLoginButton: React.FC<Props> = ({
  onCredential,
  disabled = false,
  text = "continue_with",
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  const handleCredential = useCallback(
    (response: { credential?: string }) => {
      if (response?.credential) {
        onCredential(response.credential);
      }
    },
    [onCredential]
  );

  const isSubdomain =
    typeof window !== "undefined" &&
    window.location.hostname !== "webtransfer.app" &&
    window.location.hostname !== "localhost";

  const isKredafi =
    typeof window !== "undefined" &&
    (window.location.hostname.includes("kredafi") || window.location.hostname.includes("localhost"));

  const handleSsoGoogle = () => {
    if (isKredafi) {
      // На домене KredaFi перезапускаем инициализацию Google кнопки
      setFailed(false);
      return;
    }
    const returnTo = encodeURIComponent(window.location.origin);
    const w = 480;
    const h = 600;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      `https://webtransfer.app/api/auth/sso.php?provider=google&return_to=${returnTo}`,
      "kredafi_google_auth",
      `width=${w},height=${h},left=${left},top=${top},status=no,toolbar=no,menubar=no,location=no`
    );
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      window.location.href = `https://webtransfer.app/api/auth/sso.php?provider=google&return_to=${encodeURIComponent(window.location.href)}`;
    }
  };

  useEffect(() => {
    if (disabled || !CLIENT_ID) {
      return;
    }

    let cancelled = false;

    loadGsiScript()
      .then(() => {
        if (cancelled || !containerRef.current) {
          return;
        }

        const google = (window as any).google;
        if (!google || !google.accounts || !google.accounts.id) {
          setFailed(true);
          return;
        }

        google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: handleCredential,
          use_fedcm_for_prompt: true,
        });

        google.accounts.id.renderButton(containerRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          shape: "pill",
          text,
          width: containerRef.current.offsetWidth || 320,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [handleCredential, disabled, text]);

  if (!CLIENT_ID) {
    return (
      <div className="auth-provider-error">
        {t("Google sign-in is not configured on this build.")}
      </div>
    );
  }

  if (failed) {
    return (
      <button
        type="button"
        className="auth-sso-google-btn"
        onClick={handleSsoGoogle}
        disabled={disabled}
      >
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path
            fill="#4285F4"
            d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
          />
          <path
            fill="#34A853"
            d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
          />
          <path
            fill="#FBBC05"
            d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.98 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
          />
          <path
            fill="#EA4335"
            d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
          />
        </svg>
        <span>{text === "signin_with" ? t("Sign in with Google") : t("Continue with Google")}</span>
      </button>
    );
  }

  return <div ref={containerRef} className="auth-google-button" />;
};

export default GoogleLoginButton;
